import {
  Component,
  OnChanges,
  ElementRef,
  SimpleChanges,
  ChangeDetectorRef,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
  computed,
  signal,
  effect,
} from '@angular/core';

import { Cell, TableHead, TableRow } from './table.interface';

import { NgIcon } from '@ng-icons/core';
import { CommonModule } from '@angular/common';
import { TableHeaderComponent } from './table-header/table-header.component';
import { TableFooterComponent } from './table-footer/table-footer.component';
import { TableContext } from '../../services/table/table-context.service';
import { TableLoadingSkeletonComponent } from './table-loading-skeleton/table-loading-skeleton.component';
import { HeadRowComponent } from './head-row/head-row.component';
import { GroupColsComponent } from './group-cols/group-cols.component';
import { AdhockTableComponent } from './adhock-table/adhock-table.component';
import { BarChartData } from '../../interfaces/global/chart';
import { BarChartComponent } from '../bar-chart/bar-chart.component';
import { DictionaryService } from '../../services/dictionary.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TableFormatService } from '../../services/table/table-format.service';

export const createCustomCellContext = (
  value: Cell,
  row: TableRow,
  column: TableHead,
) => ({ $implicit: value, value, row, column });

export const guardTableCopy = (
  event: Pick<ClipboardEvent, 'preventDefault'>,
  canCopy: boolean,
): void => {
  if (!canCopy) event.preventDefault();
};

@Component({
  selector: 'app-table',
  imports: [
    CommonModule,
    TableHeaderComponent,
    TableFooterComponent,
    TableLoadingSkeletonComponent,
    HeadRowComponent,
    GroupColsComponent,
    AdhockTableComponent,
    BarChartComponent,
    NgIcon,
    LoadingSpinnerComponent,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableContext, TableFormatService],
})
export class TableComponent
  implements OnChanges, AfterViewInit, OnDestroy, OnInit {
  chartData = signal<BarChartData>({});
  // ---------------- services -----------------------

  elementRef = inject(ElementRef);
  cdr = inject(ChangeDetectorRef);
  tableContext = inject(TableContext);
  dictionaryService = inject(DictionaryService);
  // ---------------- Local Variables -------------------

  isAllSelected = computed(() => {
    return this.tableContext.tableData().rows.every((row) => row.selected);
  });

  tableCount: number;

  // ----------- Managing state changes ---------------

  constructor() {
    this.tableCount = 0;
    effect(() => {
      this.tableCount =
        this.tableContext.tableData().heads.filter((head) => !head.ignore)
          .length +
        this.tableContext.actionCol().length +
        (this.tableContext.hasIndexCol() ? 1 : 0);

      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.refresh();
  }


  refresh(): void{
    this.tableContext.initialize().subscribe({
      next: () => {
        this.tableContext.setIsLoading(false);
      },
      error: () => {
        this.tableContext.setIsLoading(false);
        this.tableContext.markInitializedAfterError();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void { }

  ngAfterViewInit(): void { }

  ngOnDestroy(): void { }

  createCtx(row: TableRow, actionColIndex: number) {
    return {
      onClick: () => this.handleButtonClick(row, actionColIndex),
      row: row
    };
  }

  createCellCtx(value: Cell, row: TableRow, column: TableHead) {
    return createCustomCellContext(value, row, column);
  }

  // ----------- public functions ----------------------------

  handleButtonClick(row: TableRow, actionColIndex: number) {
    this.tableContext.actionCol()[actionColIndex].onClick(row);
  }

  clearSelection() {
    this.tableContext.tableData().rows.forEach((row) => (row.selected = false));
    this.cdr.detectChanges();
  }

  getCellValues(cell: Cell) {
    if (typeof cell === 'object') {
      return Object.values(cell);
    }
    return [];
  }

  hasMultipleVals(cell: Cell) {
    return typeof cell === 'object';
  }

  handleSelectAll($event: Event) {
    const isChecked = ($event.target as HTMLInputElement).checked;
    this.tableContext.handleAllRowsChecked(isChecked);
  }

  handleCopy(event: ClipboardEvent): void {
    guardTableCopy(event, this.tableContext.canCopy());
  }
}
